import type { CustomPeriod, PeriodType } from "@okeys/shared-utils"
import {
  CalendarDays,
  Clock,
  Euro,
  LayoutDashboard,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { StatCard } from "../../components/Common/StatCard"
import { Badge } from "../../components/ui/badge"
import { Button } from "../../components/ui/button"
import { Calendar } from "../../components/ui/calendar"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import { Label } from "../../components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select"
import { useDashboardData } from "../../hooks/useDashboardData"

type LocalCustomPeriod = {
  startDate: Date | undefined
  endDate: Date | undefined
}

export const Dashboard = () => {
  const navigate = useNavigate()
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>("today")
  const [isCustomPeriodModalOpen, setIsCustomPeriodModalOpen] = useState(false)
  const [customPeriod, setCustomPeriod] = useState<LocalCustomPeriod>({
    startDate: undefined,
    endDate: undefined,
  })

  // Convertir la période locale vers le format attendu par le hook
  const dashboardCustomPeriod: CustomPeriod | undefined =
    selectedPeriod === "custom" &&
    customPeriod.startDate &&
    customPeriod.endDate
      ? { startDate: customPeriod.startDate, endDate: customPeriod.endDate }
      : undefined

  const { dashboardData, isLoading } = useDashboardData({
    period: selectedPeriod,
    customPeriod: dashboardCustomPeriod,
  })

  if (isLoading || !dashboardData) {
    return <div>Loading...</div>
  }

  const handlePeriodChange = (value: PeriodType) => {
    if (value === "custom") {
      setIsCustomPeriodModalOpen(true)
    } else {
      setSelectedPeriod(value)
      // TODO: Implémenter la logique pour filtrer les données selon la période
    }
  }

  const handleCustomPeriodSave = () => {
    if (customPeriod.startDate && customPeriod.endDate) {
      setSelectedPeriod("custom")
      setIsCustomPeriodModalOpen(false)
      // TODO: Implémenter la logique pour filtrer les données selon la période personnalisée
    }
  }

  const handleCustomPeriodCancel = () => {
    setIsCustomPeriodModalOpen(false)
    setCustomPeriod({ startDate: undefined, endDate: undefined })
  }

  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case "today":
        return "aujourd'hui"
      case "week":
        return "cette semaine"
      case "month":
        return "ce mois-ci"
      case "all":
        return "depuis le début"
      case "custom":
        if (customPeriod.startDate && customPeriod.endDate) {
          const start = customPeriod.startDate.toLocaleDateString("fr-FR")
          const end = customPeriod.endDate.toLocaleDateString("fr-FR")
          return `du ${start} au ${end}`
        }
        return "période personnalisée"
      default:
        return "aujourd'hui"
    }
  }

  const isCustomPeriodValid = customPeriod.startDate && customPeriod.endDate

  return (
    <div className="h-full flex flex-col space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <LayoutDashboard className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Dashboard
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Statistiques de votre boutique
            </p>
          </div>
        </div>
      </div>

      {/* Contenu avec scroll */}
      <div className="flex-1 overflow-y-auto space-y-6">
        {/* Statistiques par période */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <div className="p-1.5 bg-slate-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-slate-600" />
                  </div>
                  Statistiques par période
                </CardTitle>
                <CardDescription>Données {getPeriodLabel()}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {selectedPeriod === "custom" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsCustomPeriodModalOpen(true)}
                    className="flex items-center gap-2 border-slate-300 hover:bg-slate-50 hover:border-slate-400"
                  >
                    <CalendarDays className="h-4 w-4 text-slate-600" />
                    Modifier
                  </Button>
                )}
                <Select
                  value={selectedPeriod}
                  onValueChange={handlePeriodChange}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sélectionner une période" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Aujourd'hui</SelectItem>
                    <SelectItem value="week">Cette semaine</SelectItem>
                    <SelectItem value="month">Ce mois-ci</SelectItem>
                    <SelectItem value="all">Toutes les données</SelectItem>
                    <SelectItem value="custom">
                      Période personnalisée
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <StatCard
                title="Rendez-vous"
                value={dashboardData.todayStats.appointments}
                description="Basé sur vos données réelles"
                icon={CalendarDays}
                color="blue"
              />

              <StatCard
                title="Chiffre d'affaires"
                value={`${dashboardData.todayStats.revenue}€`}
                description="Calculé automatiquement"
                icon={Euro}
                color="green"
              />

              <StatCard
                title="Clients"
                value={dashboardData.todayStats.clients}
                description="Clients uniques"
                icon={Users}
                color="purple"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contenu principal */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
          {/* Services populaires */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Zap className="h-5 w-5 text-orange-600" />
                </div>
                Services populaires
              </CardTitle>
              <CardDescription>Basé sur toutes vos données</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardData.popularServices.length > 0 ? (
                  dashboardData.popularServices.map((service, index) => (
                    <div
                      key={service.name}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-2">
                        <Badge
                          variant="secondary"
                          className={`w-6 h-6 p-0 flex items-center justify-center text-xs ${
                            index === 0
                              ? "bg-orange-100 text-orange-700 border-orange-200"
                              : index === 1
                              ? "bg-amber-100 text-amber-700 border-amber-200"
                              : index === 2
                              ? "bg-yellow-100 text-yellow-700 border-yellow-200"
                              : "bg-gray-100 text-gray-700 border-gray-200"
                          }`}
                        >
                          {index + 1}
                        </Badge>
                        <div>
                          <p className="text-sm font-medium">{service.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {service.bookings} réservations
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">
                          {service.revenue}€
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Aucune donnée disponible
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Prochains rendez-vous */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Clock className="h-5 w-5 text-indigo-600" />
                </div>
                Prochains rendez-vous
              </CardTitle>
              <CardDescription>
                Vos prochains rendez-vous confirmés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboardData.upcomingAppointments.length > 0 ? (
                  dashboardData.upcomingAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="px-2 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-md">
                          {appointment.time}
                        </div>
                        <div>
                          <p className="font-medium">{appointment.client}</p>
                          <p className="text-sm text-muted-foreground">
                            {appointment.service}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{appointment.price}€</p>
                        <p className="text-sm text-muted-foreground">
                          {appointment.duration} min
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Aucun rendez-vous à venir
                  </p>
                )}
              </div>
              <Button
                className="w-full mt-4"
                variant="outline"
                onClick={() => navigate("/bookings")}
              >
                Voir tous les rendez-vous
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal pour période personnalisée */}
      <Dialog
        open={isCustomPeriodModalOpen}
        onOpenChange={setIsCustomPeriodModalOpen}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sélectionner une période personnalisée</DialogTitle>
            <DialogDescription>
              Choisissez une date de début et une date de fin pour analyser vos
              données sur une période spécifique.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            <div className="space-y-2">
              <Label>Date de début</Label>
              <Calendar
                mode="single"
                selected={customPeriod.startDate}
                onSelect={(date) =>
                  setCustomPeriod((prev) => ({ ...prev, startDate: date }))
                }
                className="rounded-md border"
                disabled={(date) => {
                  if (date > new Date()) return true
                  if (customPeriod.endDate && date > customPeriod.endDate)
                    return true
                  return false
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Date de fin</Label>
              <Calendar
                mode="single"
                selected={customPeriod.endDate}
                onSelect={(date) =>
                  setCustomPeriod((prev) => ({ ...prev, endDate: date }))
                }
                className="rounded-md border"
                disabled={(date) => {
                  if (date > new Date()) return true
                  if (customPeriod.startDate && date < customPeriod.startDate)
                    return true
                  return false
                }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCustomPeriodCancel}>
              Annuler
            </Button>
            <Button
              onClick={handleCustomPeriodSave}
              disabled={!isCustomPeriodValid}
            >
              Appliquer la période
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
